/**
 * Row-level diffing for whole-store saves.
 *
 * The store adapters receive a COMPLETE store on every save (see
 * StoreAdapter.save). Writing all of it every time would mean hundreds of
 * upserts per keystroke, so each save is diffed against the last one written
 * and only the rows that actually changed are sent.
 *
 * The previous snapshot is held in memory per store id. On the first save
 * after a page load there is no snapshot, so everything is written once --
 * correct, if not minimal. That is the right trade: a stale snapshot causing
 * a MISSED write would be silent data loss, whereas a redundant write is just
 * slower.
 */

export interface RowDiff<T> {
  inserted: T[]
  updated: T[]
  deletedIds: string[]
}

/** Cheap structural equality. Key order is stable because rows are built by
 *  the same mapper every time, so JSON comparison is safe here. */
function same(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

/**
 * Diffs two id-keyed row maps.
 *
 * `next` is the desired state; `prev` is what was last written (or null on a
 * cold start, which forces a full write).
 */
export function diffRows<T extends { id: string }>(
  prev: Record<string, T> | null,
  next: Record<string, T>,
): RowDiff<T> {
  const inserted: T[] = []
  const updated: T[] = []

  for (const id of Object.keys(next)) {
    const before = prev?.[id]
    if (!before) inserted.push(next[id])
    // The same row object is unchanged; only new objects need comparing
    else if (before !== next[id] && !same(before, next[id])) updated.push(next[id])
  }

  const deletedIds = prev
    ? Object.keys(prev).filter(id => !(id in next))
    : []

  return { inserted, updated, deletedIds }
}

/** True when a diff would write nothing, so the caller can skip the round trip. */
export function isEmptyDiff<T>(d: RowDiff<T>): boolean {
  return d.inserted.length === 0 && d.updated.length === 0 && d.deletedIds.length === 0
}

/**
 * Snapshot cache, keyed by `${storeId}:${table}`.
 *
 * Module-level rather than per-adapter so a remounted hook does not lose it
 * and trigger a full rewrite. Cleared explicitly on team switch, where the
 * snapshot genuinely no longer describes what is in the database.
 */
const snapshots = new Map<string, Record<string, any>>()

export function getSnapshot<T>(key: string): Record<string, T> | null {
  return (snapshots.get(key) as Record<string, T>) ?? null
}

export function putSnapshot<T>(key: string, rows: Record<string, T>): void {
  // Structured clone: the caller keeps mutating its store, and a shared
  // reference here would make the snapshot silently track those edits,
  // so the next diff would see no change and skip a real write.
  // Rows that already are this snapshot's own objects are kept, not cloned again
  const prev = snapshots.get(key)
  const out: Record<string, T> = {}
  for (const [id, row] of Object.entries(rows)) out[id] = prev && prev[id] === row ? row : structuredClone(row)
  snapshots.set(key, out)
}

export function clearSnapshots(prefix?: string): void {
  if (!prefix) { snapshots.clear(); return }
  for (const key of [...snapshots.keys()]) {
    if (key.startsWith(prefix)) snapshots.delete(key)
  }
}
